import React, {useEffect} from 'react';

import Snackbar from '@material-ui/core/Snackbar';
import {constants} from '@newsela/angelou';
import {css} from 'emotion';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';


import {deleteMessage} from 'modulesV2/actions';
import {getMessage} from 'modulesV2/selectors';
import withTrackEvent from 'static/three-oh/src/components/with/WithTrackEvent';
import fonts from 'static/three-oh/src/modules/utils/fonts';

const {colors} = constants;
const SnackbarWithTrackEvent = withTrackEvent(Snackbar);

const propTypes = {
  message: PropTypes.object,
  actions: PropTypes.object,
};

/**
 * Messages that should be shown at the highest level of the application tree.
 * Messages must appear in a consistent location across all interactions within the application.
 * This is to adhere WCAG compliance (3.2.4 Consistent Identification)
 * Source: https://www.w3.org/TR/UNDERSTANDING-WCAG20/consistent-behavior-consistent-functionality.html
 */

export function MessageContainer({message = {}, actions} = {}) {
  const handleClose = (event, reason) => {
    // Ensures snackbar doesn't close if we click anywhere else
    if (reason === 'clickaway') { return; }
    actions.deleteMessage();
  };

  function duration() {
    const durationString = message.duration;
    switch (durationString) {
      case 'long':
        return 12000;
      case 'medium':
        return 5000;
      case 'never': // dismissable only through an action
        return null;
      case 'regular':
      default:
        return 3500;
    }
  }

  useEffect(() => {
    // When we unmount the component, dismiss the snackbar.
    return () => actions.deleteMessage();
  }, []);

  const {
    action,
    open,
    status,
    text,
  } = message;

  const rootCss = css({
    '&&': {
      backgroundColor: status ? colors.status[status][500] : colors.status.info[500],
      fontFamily: fonts.circular.normal,
      fontWeight: 'bold',
      textAlign: 'center',
      flexGrow: 'inherit',
    }
  });

  const actionCss = css({
    '&, *:visited': {
      fontFamily: fonts.circular.normal,
      fontWeight: 700,
      color: colors.ui.white[500],
      textDecoration: 'underline',
      textTransform: 'capitalize',
      ':hover': {
        color: colors.ui.white[500],
        textDecoration: 'underline'
      }
    }
  });
  return (
    <div role="alert" aria-live="Assertive">
      <SnackbarWithTrackEvent
        actionName={message.status == 'danger' ? 'Error' : 'Success'} // Ignore 'info' message type for now.
        streamProperties={{description: message.text}}
        action={action}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={open}
        onClose={handleClose}
        message={text}
        autoHideDuration={duration()}
        ContentProps={{
          classes: {
            // Applies styles to action components
            // Specificity only works for components and not plain text
            action: actionCss,
            root: rootCss
          }
        }}
        data-qa-selector="snackbar"
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    message: getMessage(state),
  };
};

const actions = {
  deleteMessage,
};

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(actions, dispatch),
  };
}

MessageContainer.propTypes = propTypes;
export default connect(mapStateToProps, mapDispatchToProps)(MessageContainer);
